import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { RentFormInput } from "./components/RentFormInput";
import { RentFormHeader } from "./components/RentFormHeader";
import { AddressFields } from "./components/AddressFields";
import { ProrataBlock } from "./components/ProrataBlock";
import { LegalNoticeLong, MissingInfoWarning } from "./components/LegalNotice";
import { getDwellingsForAddress } from "./services/DwellingsAPI";
import { searchPermits } from "./services/PermitSearchAPI";
import { searchPropertyTax, fetchPropertyTaxByAccount } from "./services/MontrealTaxAPI";
import { 
  getConfig, 
  calcBaseIPC, 
  calcCombinedTaxImpact, 
  calcInsuranceImpact, 
  calcWorksImpact, 
  formatCurrency,
  formatPercent
} from "./engine/calc2026";
import { BrandButton } from "./components/BrandButton";
import { ContextualExplanation } from "./components/ContextualExplanation";
import { BreakdownSummary } from "./components/BreakdownSummary";

const isDev = process.env.NODE_ENV !== 'production';
const debugLog = (...args) => {
  if (isDev) console.log(...args);
};

const STEPS = {
  ADDRESS: 1,
  IPC_AND_TAXES: 2,
  INSURANCE: 3,
  WORKS: 4,
  SUMMARY: 5
};

export const RentForm2026 = () => {
  const { t } = useTranslation();
  const methods = useForm();
  const config = getConfig();
  const formRef = useRef(null);
  const validStepsRef = useRef(new Set(Object.values(STEPS)));

  const [currentStep, setCurrentStep] = useState(STEPS.ADDRESS);
  const [isLoading, setIsLoading] = useState(false);
  const [currentRent, setCurrentRent] = useState(0);
  const [estimatedRent, setEstimatedRent] = useState(0);
  const [prorata, setProrata] = useState(null);
  const [detectedUnits, setDetectedUnits] = useState(1);
  const [permits, setPermits] = useState([]);
  
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [appNumber, setAppNumber] = useState("");
  const [arrondissement, setArrondissement] = useState("");
  
  const [taxData, setTaxData] = useState({
    municipal2025: 0,
    municipal2026: 0,
    school2025: 0,
    school2026: 0,
    accountAddress: '',
    taxId: ''
  });
  const [taxCandidates, setTaxCandidates] = useState([]);
  const [selectedTaxIndex, setSelectedTaxIndex] = useState('');
  const [isTaxAccountLoading, setIsTaxAccountLoading] = useState(false);
  
  const [insuranceAnswer, setInsuranceAnswer] = useState('unknown');
  const [insuranceData, setInsuranceData] = useState({
    before: 0,
    after: 0
  });
  
  const [worksData, setWorksData] = useState({
    hasWorks: false,
    admissibleCost: 0
  });
  const [arrondissementError, setArrondissementError] = useState('');
  const [landlordProposedRent, setLandlordProposedRent] = useState('');

  const [breakdown, setBreakdown] = useState({
    baseIPC: 0,
    municipalTax: 0,
    schoolTax: 0,
    taxes: 0,
    insurance: 0,
    insuranceUnknown: false,
    works: 0,
    total: 0
  });
  
  // Shared color schemes to align recap rows and contextual explanations
  const colorSchemes = {
    ipc: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
    taxesImpact: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
    taxesNone: { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600" },
    insuranceImpact: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
    insuranceUnknown: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
    insuranceNone: { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-600" },
  };
  
  const [prorataAsked, setProrataAsked] = useState(false);
  const [showWorksInfo, setShowWorksInfo] = useState(false);
  const landlordProposedRentValue = parseFloat(landlordProposedRent);
  const hasLandlordProposedRent = landlordProposedRent && landlordProposedRentValue > 0;
  const isLandlordHigher = hasLandlordProposedRent && landlordProposedRentValue > estimatedRent;
  const homeTrackedRef = useRef(false);
  const summaryTrackedRef = useRef(false);

  const trackPageView = useCallback((page) => {
    if (typeof window === "undefined") return;
    fetch("/api/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ page }),
      keepalive: true,
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (homeTrackedRef.current) return;
    homeTrackedRef.current = true;
    trackPageView("home");
  }, [trackPageView]);

  useEffect(() => {
    if (currentStep !== STEPS.SUMMARY) return;
    if (summaryTrackedRef.current) return;
    summaryTrackedRef.current = true;
    trackPageView("summary");
  }, [currentStep, trackPageView]);

  const applyTaxCandidate = (candidate) => {
    if (!candidate) return;
    const taxInfo = {
      municipal2025: parseFloat(candidate.municipalTaxPreviousYear ?? candidate.municipalTaxTwoYearsAgo) || 0,
      municipal2026: parseFloat(candidate.municipalTaxCurrentYear ?? candidate.municipalTaxLastYear) || 0,
      school2025: parseFloat(candidate.schoolTaxPreviousYear ?? candidate.schoolTaxAmount2024) || 0,
      school2026: parseFloat(candidate.schoolTaxCurrentYear ?? candidate.schoolTaxAmount2025) || 0,
      accountAddress: candidate.address || '',
      taxId: candidate.taxId || ''
    };
    setTaxData(taxInfo);
    methods.setValue('municipalTax2025', taxInfo.municipal2025);
    methods.setValue('municipalTax2026', taxInfo.municipal2026);
    methods.setValue('schoolTax2025', taxInfo.school2025);
    methods.setValue('schoolTax2026', taxInfo.school2026);
  };

  const applyTaxAccountSelection = async (candidate) => {
    if (!candidate?.taxId || !arrondissement) {
      applyTaxCandidate(candidate);
      return;
    }
    setIsTaxAccountLoading(true);
    try {
      const response = await fetchPropertyTaxByAccount({
        taxAccountId: candidate.taxId,
        resourceId: arrondissement
      });
      if (response.success && response.data) {
        applyTaxCandidate(response.data);
        return;
      }
      applyTaxCandidate(candidate);
    } catch (error) {
      console.error('Error fetching tax account details:', error);
      applyTaxCandidate(candidate);
    } finally {
      setIsTaxAccountLoading(false);
    }
  };

  const getApiTaxImpact = () => {
    if (!taxData.municipal2025 && !taxData.municipal2026 && !taxData.school2025 && !taxData.school2026) {
      return { hasTaxImpact: false, municipal: 0, school: 0 };
    }
    const share = prorata?.share || (1 / detectedUnits);
    const impact = calcCombinedTaxImpact(
      taxData.municipal2025, 
      taxData.municipal2026, 
      taxData.school2025, 
      taxData.school2026, 
      share
    );
    return { 
      hasTaxImpact: impact.amount > 0,
      municipal: impact.municipal?.amount || 0,
      school: impact.school?.amount || 0,
      total: impact.amount
    };
  };

  const getStepMinimumNotice = () => {
    if (currentStep === STEPS.IPC_AND_TAXES) {
      return t(
        '2026.ipcMinimumNotice',
        "Votre loyer peut \u00EAtre augment\u00E9 d'au moins {{amount}} par mois \u00E0 cause de l'IPC.",
        { amount: formatCurrency(breakdown.baseIPC) }
      );
    }

    if (currentStep === STEPS.INSURANCE) {
      return t(
        '2026.taxesMinimumNotice',
        "Votre loyer peut \u00EAtre augment\u00E9 d'au moins {{amount}} par mois \u00E0 cause des taxes.",
        { amount: formatCurrency(breakdown.taxes) }
      );
    }

    if (currentStep === STEPS.WORKS && !breakdown.insuranceUnknown) {
      return t(
        '2026.insuranceMinimumNotice',
        "Votre loyer peut \u00EAtre augment\u00E9 d'au moins {{amount}} par mois \u00E0 cause des assurances.",
        { amount: formatCurrency(breakdown.insurance) }
      );
    }

    if (currentStep === STEPS.SUMMARY) {
      return null;
    }

    return null;
  };

  const scrollToTop = useCallback(() => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const isValidStep = useCallback((step) => validStepsRef.current.has(step), []);

  const syncHistoryStep = useCallback((step, mode = 'push') => {
    if (typeof window === 'undefined') return;
    const nextStep = isValidStep(step) ? step : STEPS.ADDRESS;
    const state = { step: nextStep };
    const url = window.location.href;
    if (mode === 'replace') {
      window.history.replaceState(state, '', url);
      return;
    }
    window.history.pushState(state, '', url);
  }, [isValidStep]);

  const goToStep = (step, { replaceHistory = false } = {}) => {
    const nextStep = isValidStep(step) ? step : STEPS.ADDRESS;
    setCurrentStep(nextStep);
    syncHistoryStep(nextStep, replaceHistory ? 'replace' : 'push');
    setTimeout(scrollToTop, 100);
  };

  const handleStep1Submit = async () => {
    const validRent = await methods.trigger(['rent']);
    if (!arrondissement) {
      setArrondissementError(t('2026.address.selectBorough', 'Choisir un arrondissement'));
    } else {
      setArrondissementError('');
    }
    if (!validRent || !arrondissement) return;

    const rent = parseFloat(methods.getValues('rent')) || 0;

    setIsLoading(true);
    setCurrentRent(rent);
    setTaxCandidates([]);
    setSelectedTaxIndex('');

    const baseIncrease = calcBaseIPC(rent);
    setEstimatedRent(rent + baseIncrease);
    setBreakdown(prev => ({ ...prev, baseIPC: baseIncrease }));

    // Fetch address data and wait for completion before navigating
    if (arrondissement && streetNumber && streetName) {
      try {
        const taxPromise = (async () => {
          let foundData = null;
          const trimmedStreetNumber = String(streetNumber || '').trim();
          const numericMatch = trimmedStreetNumber.match(/\d+/);
          const baseNumber = numericMatch ? numericMatch[0] : '';
          if (!baseNumber) return;

          const response = await searchPropertyTax({
            streetNumber: baseNumber,
            streetName,
            appNumber,
            resourceId: arrondissement
          });

          if (response.success && response.data.length > 0) {
            foundData = response.data;
          }

          if (foundData && foundData.length === 1) {
            await applyTaxAccountSelection(foundData[0]);
            setTaxCandidates([]);
            setSelectedTaxIndex('0');
          } else if (foundData && foundData.length > 1) {
            setTaxCandidates(foundData);
            setSelectedTaxIndex('');
            setTaxData(prev => ({
              ...prev,
              municipal2025: 0,
              municipal2026: 0,
              school2025: 0,
              school2026: 0,
              accountAddress: '',
              taxId: ''
            }));
            methods.setValue('municipalTax2025', 0);
            methods.setValue('municipalTax2026', 0);
            methods.setValue('schoolTax2025', 0);
            methods.setValue('schoolTax2026', 0);
          }
        })();

        const dwellingsPromise = (async () => {
          const dwellingsResult = await getDwellingsForAddress(arrondissement, streetName, streetNumber);
          debugLog('Dwellings API result:', dwellingsResult);
          if (dwellingsResult.success && dwellingsResult.dwellings > 0) {
            setDetectedUnits(dwellingsResult.dwellings);
            debugLog('Detected units:', dwellingsResult.dwellings);
          }
        })();

        const permitsPromise = (async () => {
          const permitsResult = await searchPermits({ streetNumber, streetName });
          debugLog('Permits API result:', permitsResult);
          if (permitsResult.success && permitsResult.allRecords) {
            const currentYear = new Date().getFullYear();
            const recentPermits = permitsResult.allRecords.filter(p => {
              const permitDate = p.DATE_PERMIS || p.date_debut || p.date_permis || '';
              const year = parseInt(permitDate.substring(0, 4));
              return year >= currentYear - 2;
            });
            debugLog('Filtered recent permits:', recentPermits);
            setPermits(recentPermits);
          }
        })();

        await Promise.all([taxPromise, dwellingsPromise, permitsPromise]);
      } catch (err) {
        console.error('Error fetching address data:', err);
      }
    }

    setIsLoading(false);
    goToStep(STEPS.IPC_AND_TAXES);
  };

  const handleIPCAndTaxesSubmit = () => {
    const municipal2025 = parseFloat(methods.getValues('municipalTax2025')) || 0;
    const municipal2026 = parseFloat(methods.getValues('municipalTax2026')) || 0;
    const school2025 = parseFloat(methods.getValues('schoolTax2025')) || taxData.school2025 || 0;
    const school2026 = parseFloat(methods.getValues('schoolTax2026')) || taxData.school2026 || 0;
    
    setTaxData(prev => ({
      ...prev,
      municipal2025,
      municipal2026,
      school2025,
      school2026
    }));

    const share = prorata?.share || (1 / detectedUnits);
    const taxImpact = calcCombinedTaxImpact(municipal2025, municipal2026, school2025, school2026, share);

    setBreakdown(prev => ({
      ...prev,
      municipalTax: taxImpact.municipal.amount,
      schoolTax: taxImpact.school.amount,
      taxes: taxImpact.amount
    }));
    setEstimatedRent(currentRent + breakdown.baseIPC + taxImpact.amount);
    
    setProrataAsked(true);
    goToStep(STEPS.INSURANCE);
  };

  const handleTaxCandidateChange = (event) => {
    const value = event.target.value;
    setSelectedTaxIndex(value);
    const index = parseInt(value, 10);
    if (!Number.isNaN(index) && taxCandidates[index]) {
      applyTaxAccountSelection(taxCandidates[index]);
    }
  };

  const handleInsuranceSubmit = () => {
    let insuranceAmount = 0;
    const isUnknown = insuranceAnswer === 'unknown';
    
    if (insuranceAnswer === 'yes') {
      const before = parseFloat(insuranceData.before) || 0;
      const after = parseFloat(insuranceData.after) || 0;
      const share = prorata?.share || (1 / detectedUnits);
      const impact = calcInsuranceImpact(before, after, share);
      insuranceAmount = impact.amount;
    }
    
    setBreakdown(prev => ({
      ...prev,
      insurance: insuranceAmount,
      insuranceUnknown: isUnknown
    }));
    setEstimatedRent(currentRent + breakdown.baseIPC + breakdown.taxes + insuranceAmount);
    
    setProrataAsked(true);
    goToStep(STEPS.WORKS);
  };

  const handleWorksSubmit = () => {
    let worksAmount = 0;
    
    if (worksData.hasWorks && worksData.admissibleCost > 0) {
      const share = prorata?.share || (1 / detectedUnits);
      const impact = calcWorksImpact(worksData.admissibleCost, share);
      worksAmount = impact.amount;
    }
    
    setBreakdown(prev => ({
      ...prev,
      works: worksAmount
    }));
    
    const totalIncrease = breakdown.baseIPC + breakdown.taxes + breakdown.insurance + worksAmount;
    setEstimatedRent(currentRent + totalIncrease);
    setBreakdown(prev => ({
      ...prev,
      total: totalIncrease
    }));
    
    goToStep(STEPS.SUMMARY);
  };

  const handleProrataChange = (newProrata) => {
    setProrata(newProrata);
  };

  const goBack = () => {
    if (currentStep > STEPS.ADDRESS) {
      if (typeof window !== 'undefined') {
        window.history.back();
      } else {
        goToStep(currentStep - 1, { replaceHistory: true });
      }
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const initialStep = isValidStep(window.history.state?.step)
      ? window.history.state.step
      : STEPS.ADDRESS;

    if (initialStep !== currentStep) {
      setCurrentStep(initialStep);
    }

    syncHistoryStep(initialStep, 'replace');

    const handlePopState = (event) => {
      const nextStep = isValidStep(event.state?.step) ? event.state.step : STEPS.ADDRESS;
      setCurrentStep(nextStep);
      setTimeout(scrollToTop, 100);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentStep, isValidStep, syncHistoryStep, scrollToTop]);

  const renderProgressBar = () => {
    if (currentStep === STEPS.SUMMARY) return null;
    const totalSteps = 5;
    const progress = (currentStep / totalSteps) * 100;
    
    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div 
          className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    );
  };

  const renderIntro = () => {
    return (
      <>
        <header className="w-full mb-4">
          <h1 className={`title text-4xl text-center font-extrabold ${currentStep !== STEPS.ADDRESS ? "hide-mobile-after-step1" : ""}`}>
            {t("title")}
          </h1>
        </header>
        {currentStep === STEPS.ADDRESS && (
          <div className="max-w-2xl mt-2 mb-4 text-sm sm:text-base space-y-2 sm:space-y-3">
            <div className="flex items-center gap-2">
              <img
                src="/images/profil-cercle.png"
                alt="Nicolas Jolicoeur"
                className="w-6 h-6 rounded-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span>Par <a href="https://jolicoeurmtl.com" target="_blank" rel="noreferrer" className="underline">Nicolas Jolicoeur</a>, conseiller d'arrondissement</span>
            </div>
            <div dangerouslySetInnerHTML={{ __html: t("intro.line1") }} />
            <div dangerouslySetInnerHTML={{ __html: t("intro.line2") }} />
            <div dangerouslySetInnerHTML={{ __html: t("intro.line3") }} />
            <hr className="mt-4 sm:mt-6 bg-emerald-900" />
          </div>
        )}
      </>
    );
  };

  const minimumNotice = getStepMinimumNotice();

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={(e) => e.preventDefault()}
        noValidate
        autoComplete="off"
        className="container p-4 sm:p-10 min-h-max flex flex-col items-center justify-center text-gray-800 text-sm max-w-3xl brand-shell"
      >
        {renderIntro()}
        {renderProgressBar()}
        {minimumNotice && (
          <p className="w-full mb-2 text-sm text-sky-700 notice-text-safe">
            {minimumNotice.normalize('NFC')}
          </p>
        )}
        {currentStep !== STEPS.SUMMARY && (
          <BreakdownSummary
            currentStep={currentStep}
            STEPS={STEPS}
            breakdown={breakdown}
            currentRent={currentRent}
            formatCurrency={formatCurrency}
            formatPercent={formatPercent}
            t={t}
            config={config}
            highlight={{
              ipc: currentStep === STEPS.IPC_AND_TAXES,
              taxes: currentStep === STEPS.INSURANCE,
              insurance: currentStep === STEPS.WORKS,
              works: currentStep === STEPS.SUMMARY,
              class:
                currentStep === STEPS.IPC_AND_TAXES
                  ? colorSchemes.ipc.text
                  : currentStep === STEPS.INSURANCE
                  ? colorSchemes.taxesImpact.text
                  : currentStep === STEPS.WORKS
                  ? colorSchemes.insuranceImpact.text
                  : colorSchemes.taxesImpact.text,
            }}
          />
        )}
        <ContextualExplanation
          currentStep={currentStep}
          STEPS={STEPS}
          breakdown={breakdown}
          config={config}
          t={t}
          colorSchemes={colorSchemes}
          formatCurrency={formatCurrency}
        />

        {currentStep === STEPS.ADDRESS && (
          <div className="w-full">
            <RentFormHeader text={t('2026.step1.title', 'Étape 1: Votre logement')} />
            
            <AddressFields
              streetNumber={streetNumber}
              setStreetNumber={setStreetNumber}
              streetName={streetName}
              setStreetName={setStreetName}
              appNumber={appNumber}
              setAppNumber={setAppNumber}
              arrondissement={arrondissement}
              setArrondissement={setArrondissement}
              arrondissementError={arrondissementError}
            />
            
            <div className="mt-6">
              <RentFormInput
              currency="true"
              label={<>Loyer mensuel <u>actuel</u></>}
              type="number"
              id="rent"
              placeholder="0.00"
              name="rent"
              info={t('2026.rent.info', 'Votre loyer avant l\'augmentation proposée')}
              validation={{
                required: { value: true, message: t('requis') },
                min: { value: 1, message: t('2026.rent.minError', 'Le loyer doit être supérieur à 0') }
                }}
              />
            </div>

            <div className="mt-4">
              <RentFormInput
              currency="true"
              label={<>Loyer <u>proposé</u> par le propriétaire (optionnel)</>}
              type="number"
              id="landlordRent"
              placeholder="0.00"
              name="landlordRent"
              info={t('2026.landlordRent.info', 'Si votre propriétaire vous a envoyé un avis d\'augmentation, inscrivez le nouveau loyer proposé')}
              onChange={(e) => setLandlordProposedRent(e.target.value)}
              />
            </div>
            
            <div className="flex gap-4 mt-6">
              <BrandButton
                type="button"
                onClick={handleStep1Submit}
                disabled={isLoading}
              >
                {t('continue', 'Continuer')}
              </BrandButton>
            </div>

            {isLoading && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <div>
                    <p className="text-blue-800 font-medium">{t('loading.title', 'Recherche en cours...')}</p>
                    <p className="text-blue-600 text-sm">{t('loading.description', 'Nous recherchons les données de taxes municipales et scolaires pour votre adresse.')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === STEPS.IPC_AND_TAXES && (
          <div className="w-full">
            <RentFormHeader text={t('2026.step2.title', 'Étape 2: Augmentation de base et taxes')} />
            
            <p className="text-gray-600 mb-4">
              {t('2026.step2.taxIntro', "Les variations de taxes peuvent aussi influencer le calcul.")}
            </p>
            
            {taxCandidates.length > 1 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-amber-900 text-sm mb-3">
                  {t('2026.step2.multipleTaxAccounts', "Plusieurs comptes de taxes ont été trouvés pour cette adresse. Choisissez celui qui correspond à votre logement pour préremplir les montants.")}
                </p>
                <label className="block text-sm font-medium text-amber-900 mb-1">
                  {t('2026.step2.taxAccountLabel', 'Compte de taxes')}
                </label>
                <select
                  value={selectedTaxIndex}
                  onChange={handleTaxCandidateChange}
                  className="w-full p-2 border rounded focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                >
                  <option value="">{t('2026.step2.taxAccountPlaceholder', 'Choisir un compte')}</option>
                  {taxCandidates.map((candidate, index) => (
                    <option key={`${candidate.taxId || candidate.address || index}`} value={index}>
                      {candidate.address || t('2026.step2.taxAccountUnknown', 'Adresse inconnue')}
                      {candidate.taxId ? ` — ${candidate.taxId}` : ''}
                    </option>
                  ))}
                </select>
                {isTaxAccountLoading && (
                  <p className="text-xs text-amber-800 mt-2">
                    {t('2026.step2.taxAccountLoading', 'Chargement des données du compte sélectionné...')}
                  </p>
                )}
              </div>
            )}

            {taxData.municipal2026 > 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-green-800 text-sm">
                  {taxData.accountAddress
                    ? t('2026.step2.prefilledWithAddress', 'Données préremplies à partir des registres publics de Montréal pour le compte de taxe associé à {{address}}. Vous pouvez modifier si nécessaire.', { address: taxData.accountAddress })
                    : streetNumber && streetName
                      ? t('2026.step2.prefilledWithAddress', 'Données préremplies à partir des registres publics de Montréal pour le compte de taxe associé à {{address}}. Vous pouvez modifier si nécessaire.', { address: `${streetNumber} ${streetName}` })
                      : t('2026.step2.prefilled', 'Données préremplies à partir des registres publics de Montréal. Vous pouvez les modifier si nécessaire.')
                  }
                </p>
              </div>
            ) : (
              taxCandidates.length === 0 && (
                <MissingInfoWarning message={t('2026.step2.notFound', 'Données de taxes non trouvées automatiquement. Veuillez les entrer manuellement si vous les connaissez, ou laissez à 0.')} />
              )
            )}
            
            <h4 className="font-semibold mt-4 mb-2">{t('2026.municipalTaxes', 'Taxes municipales')}</h4>
            
            <RentFormInput
              currency="true"
              label={t('2026.tax2025.label', 'Taxes municipales 2025')}
              type="number"
              id="municipalTax2025"
              placeholder="0.00"
              name="municipalTax2025"
            />
            
            <RentFormInput
              currency="true"
              label={t('2026.tax2026.label', 'Taxes municipales 2026')}
              type="number"
              id="municipalTax2026"
              placeholder="0.00"
              name="municipalTax2026"
            />
            
            <h4 className="font-semibold mt-6 mb-2">{t('2026.schoolTaxes', 'Taxes scolaires')}</h4>
            
            <RentFormInput
              currency="true"
              label={t('2026.schoolTax2025.label', 'Taxe scolaire 2024-2025')}
              type="number"
              id="schoolTax2025"
              placeholder="0.00"
              name="schoolTax2025"
            />
            
            <RentFormInput
              currency="true"
              label={t('2026.schoolTax2026.label', 'Taxe scolaire 2025-2026')}
              type="number"
              id="schoolTax2026"
              placeholder="0.00"
              name="schoolTax2026"
            />

            {detectedUnits > 1 && !prorataAsked && getApiTaxImpact().hasTaxImpact && (
              <div className="mt-6 border-t pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    <strong>{t('2026.prorata.needed', 'Répartition entre logements:')}</strong>{' '}
                    {t('2026.prorata.explanationTaxes', `Nous avons détecté ${detectedUnits} logements à cette adresse. L'impact des taxes doit être réparti entre les logements.`)}
                  </p>
                </div>
                <ProrataBlock
                  userRent={currentRent}
                  detectedUnits={detectedUnits}
                  onProrataChange={handleProrataChange}
                />
              </div>
            )}
            
            <div className="flex gap-4 mt-6">
              <BrandButton
                type="button"
                onClick={goBack}
                variant="outline"
                className="flex-1"
              >
                {t('back', 'Retour')}
              </BrandButton>
              <BrandButton
                type="button"
                onClick={handleIPCAndTaxesSubmit}
                className="flex-1"
              >
                {t('continue', 'Continuer')}
              </BrandButton>
            </div>
          </div>
        )}

        {currentStep === STEPS.INSURANCE && (
          <div className="w-full">
            <RentFormHeader text={t('2026.step3.title', 'Étape 3: Assurances')} />
            
            <p className="text-gray-600 mb-4">
              {t('2026.step5.question', 'Les assurances incendie et responsabilité du propriétaire ont-elles augmenté entre 2025 et 2026?')}
            </p>
            
            <div className="flex flex-col gap-2 mb-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded hover:bg-gray-50">
                <input
                  type="radio"
                  name="insuranceAnswer"
                  value="no"
                  checked={insuranceAnswer === 'no'}
                  onChange={() => setInsuranceAnswer('no')}
                  className="text-emerald-600"
                />
                <span>{t('no', 'Non')}</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded hover:bg-gray-50">
                <input
                  type="radio"
                  name="insuranceAnswer"
                  value="unknown"
                  checked={insuranceAnswer === 'unknown'}
                  onChange={() => setInsuranceAnswer('unknown')}
                  className="text-emerald-600"
                />
                <span>{t('unknown', 'Je ne sais pas')}</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded hover:bg-gray-50">
                <input
                  type="radio"
                  name="insuranceAnswer"
                  value="yes"
                  checked={insuranceAnswer === 'yes'}
                  onChange={() => setInsuranceAnswer('yes')}
                  className="text-emerald-600"
                />
                <span>{t('yes', 'Oui')}</span>
              </label>
            </div>

            {insuranceAnswer === 'yes' && (
              <div className="border-t pt-4 mt-4">
                <RentFormInput
                  currency="true"
                  label={<>Assurances incendie et responsabilité <strong>ANNUELLES</strong> au 31 décembre 2024</>}
                  type="number"
                  id="insuranceBefore"
                  placeholder="0.00"
                  name="insuranceBefore"
                  onChange={(e) => setInsuranceData(prev => ({ ...prev, before: e.target.value }))}
                />
                <RentFormInput
                  currency="true"
                  label={<>Assurances incendie et responsabilité <strong>ANNUELLES</strong> au 31 décembre 2025</>}
                  type="number"
                  id="insuranceAfter"
                  placeholder="0.00"
                  name="insuranceAfter"
                  onChange={(e) => setInsuranceData(prev => ({ ...prev, after: e.target.value }))}
                />
              </div>
            )}

            {detectedUnits > 1 && !prorataAsked && (
              <div className="mt-6 border-t pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    <strong>{t('2026.prorata.needed', 'Répartition entre logements:')}</strong>{' '}
                    {t('2026.prorata.explanation', `Nous avons détecté ${detectedUnits} logements à cette adresse. Les augmentations liées aux taxes doivent être réparties entre les logements.`)}
                  </p>
                </div>
                <ProrataBlock
                  userRent={currentRent}
                  detectedUnits={detectedUnits}
                  onProrataChange={handleProrataChange}
                />
              </div>
            )}
            
            <div className="flex gap-4 mt-6">
              <BrandButton
                type="button"
                onClick={goBack}
                variant="outline"
                className="flex-1"
              >
                {t('back', 'Retour')}
              </BrandButton>
              <BrandButton
                type="button"
                onClick={handleInsuranceSubmit}
                className="flex-1"
              >
                {t('continue', 'Continuer')}
              </BrandButton>
            </div>
          </div>
        )}

        {currentStep === STEPS.WORKS && (
          <div className="w-full">
            <RentFormHeader text={t('2026.step4.title', 'Étape 4: Réparations ou améliorations majeures')} />
            
            <p className="text-gray-600 mb-2">
              {t('2026.step6.description', 'Les travaux majeurs (toiture, fenêtres, système de chauffage, isolation, etc.) peuvent justifier une augmentation.')}
              <button
                type="button"
                onClick={() => setShowWorksInfo(!showWorksInfo)}
                className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-gray-500 bg-gray-200 rounded-full hover:bg-gray-300 focus:outline-none"
                aria-label="Plus d'informations"
              >
                i
              </button>
            </p>
            {showWorksInfo && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 text-sm text-gray-700">
                <p className="mb-2">
                  Les réparations ou améliorations majeures donnent lieu à des dépenses d'immobilisation, qui ne font pas partie des dépenses récurrentes à assumer régulièrement pour l'immeuble.
                </p>
                <p className="mb-2">
                  Il peut s'agir de travaux visant à réparer ou modifier les principaux éléments de la structure de l'immeuble (p. ex. : toit, tuyauterie, système de chauffage, isolation, fenestration, revêtement extérieur, fondation, drain français, panneau électrique) ou de travaux de rénovation (p. ex. : remplacement des armoires, comptoirs, de la plomberie, de la céramique ou du plancher) dans les logements ou les espaces communs.
                </p>
                <p>
                  L'<a
                    href="https://www.publicationsduquebec.gouv.qc.ca/fileadmin/gazette/pdf_encrypte/lois_reglements/2025F/86904.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="text-sky-700 underline hover:text-sky-900"
                  >annexe I du Règlement modifiant le Règlement sur les critères de fixation de loyer</a> prévoit également une liste de dépenses rattachées aux réparations et améliorations majeures.
                </p>
              </div>
            )}

            {permits.length > 0 ? (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <p className="font-semibold text-orange-800 mb-2">
                  {t('2026.step6.permitsFound', `${permits.length} permis détecté(s) pour cette adresse:`)}
                </p>
                <ul className="text-sm text-orange-700">
                  {permits.slice(0, 3).map((permit, index) => {
                    const date = permit.DATE_PERMIS || permit.date_debut || permit.date_permis || '';
                    const nature = permit.NATURE_TRAVAUX || permit.nature_travaux || t('2026.step6.typeUnspecified', 'Type non spécifié');
                    return (
                      <li key={index} className="mb-1">
                        {date.substring(0, 10)}: {nature}
                      </li>
                    );
                  })}
                </ul>
                <p className="text-xs text-orange-600 mt-2 italic">
                  {t('2026.step6.permitsNote', 'Ces permis sont fournis à titre indicatif seulement.')}
                </p>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="font-semibold text-green-800 mb-2">
                  {t('2026.step6.noPermitsFound', 'Aucun permis de travaux détecté dans les 2 dernières années dans le logement')}
                </p>
                <p className="text-sm text-green-700">
                  {t('2026.step6.noPermitsNote', "Plusieurs types de travaux majeurs nécessitent un permis. Si votre propriétaire réclame des travaux majeurs, vous pouvez lui demander le numéro de permis afin de valider que les travaux ont été faits selon les règles de l'arrondissement.")}
                </p>
              </div>
            )}
            
            <div className="flex flex-col gap-2 mb-4">
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded hover:bg-gray-50">
                <input
                  type="radio"
                  name="hasWorks"
                  checked={!worksData.hasWorks}
                  onChange={() => setWorksData({ hasWorks: false, admissibleCost: 0 })}
                  className="text-emerald-600"
                />
                <span>{t('2026.step6.noWorks', 'Non, pas de travaux majeurs')}</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer p-3 border rounded hover:bg-gray-50">
                <input
                  type="radio"
                  name="hasWorks"
                  checked={worksData.hasWorks}
                  onChange={() => setWorksData({ ...worksData, hasWorks: true })}
                  className="text-emerald-600"
                />
                <span>{t('2026.step6.yesWorks', 'Oui, il y a eu des travaux majeurs')}</span>
              </label>
            </div>

            {worksData.hasWorks && (
              <div className="border-t pt-4 mt-4">
                <RentFormInput
                  currency="true"
                  label={t('2026.works.cost', 'Coût admissible annuel des travaux')}
                  type="number"
                  id="worksCost"
                  placeholder="0.00"
                  name="worksCost"
                  info={t('2026.works.costInfo', "Les réparations ou améliorations majeures donnent lieu à des dépenses d’immobilisation, qui ne font pas partie des dépenses récurrentes que vous assumez régulièrement pour l’immeuble. Il peut s’agir de travaux visant à réparer ou modifier les principaux éléments de la structure de l’immeuble (p. ex. : toit, tuyauterie, système de chauffage, isolation, fenestration, revêtement extérieur, fondation, drain français, panneau électrique) ou de travaux de rénovation (p. ex. : remplacement des armoires, comptoirs, de la plomberie, de la céramique ou du plancher) dans les logements ou les espaces communs. <a href='https://www.tal.gouv.qc.ca/sites/all/themes/rdl/assets/img/Modifications-reglementaires.pdf' target='_blank' rel='noreferrer'>L’annexe I du Règlement modifiant le Règlement sur les critères de fixation de loyer</a> prévoit également une liste de dépenses rattachées aux réparations et améliorations majeures.")}
                  onChange={(e) => setWorksData(prev => ({ ...prev, admissibleCost: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            )}

            {detectedUnits > 1 && !prorataAsked && (
              <div className="mt-6 border-t pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    <strong>{t('2026.prorata.needed', 'Répartition entre logements:')}</strong>{' '}
                    {t('2026.prorata.explanationWorks', `Nous avons détecté ${detectedUnits} logements à cette adresse. Les augmentations liées aux assurances et travaux doivent être réparties entre les logements.`)}
                  </p>
                </div>
                <ProrataBlock
                  userRent={currentRent}
                  detectedUnits={detectedUnits}
                  onProrataChange={handleProrataChange}
                />
              </div>
            )}
            
            <div className="flex gap-4 mt-6">
              <BrandButton
                type="button"
                onClick={goBack}
                variant="outline"
                className="flex-1"
              >
                {t('back', 'Retour')}
              </BrandButton>
              <BrandButton
                type="button"
                onClick={handleWorksSubmit}
                className="flex-1"
              >
                {t('2026.seeResult', 'Voir le résultat')}
              </BrandButton>
            </div>
          </div>
        )}

        {currentStep === STEPS.SUMMARY && (
          <div className="w-full">
            <div className="text-center mb-6">
              <p className="uppercase tracking-wide text-base font-semibold text-gray-700 mb-2">
                {t('2026.summary.brand', 'JOLICOEURMTL.COM/LOYER')}
              </p>
            </div>

            <div className="brand-shell p-6 mb-6">
              <div className="text-center mb-4">
                <p className="text-gray-600">{t('2026.summary.newRent', 'Nouveau loyer mensuel estimé')}</p>
                <p className="text-4xl font-bold text-gray-900">{formatCurrency(estimatedRent)}</p>
                <p className="text-lg text-gray-500">
                  +{formatCurrency(breakdown.total)} ({formatPercent(breakdown.total / currentRent)})
                </p>
              </div>

              <div className="border-t border-gray-200 my-4" />
              <h3 className="font-semibold text-gray-800 mb-3">{t('2026.summary.breakdown', 'Détail de l\'estimation')}</h3>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>{t('2026.summary.previousRent', 'Loyer avant')}</span>
                  <span className="font-medium text-gray-900">{formatCurrency(currentRent)}</span>
                </div>
                
                <div className="flex justify-between text-gray-800">
                  <span>+ IPC ({config.ipcLabel})</span>
                  <span className="font-medium text-gray-900">{formatCurrency(breakdown.baseIPC)}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">+ {t('2026.breakdown.municipalTax', 'Taxes municipales')}</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(breakdown.municipalTax)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">+ {t('2026.breakdown.schoolTax', 'Taxes scolaires')}</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(breakdown.schoolTax)}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">+ {t('2026.summary.insurance', 'Assurances')}</span>
                  <span className="font-medium text-gray-900">
                    {breakdown.insuranceUnknown 
                      ? t('2026.summary.unknown', '?') 
                      : formatCurrency(breakdown.insurance)
                    }
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-gray-600">+ {t('2026.summary.works', 'Travaux')}</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(breakdown.works)}
                  </span>
                </div>
                
                <div className="border-t pt-2 mt-2 flex justify-between font-bold text-lg">
                  <span>{t('2026.summary.total', 'Total')}</span>
                  <span className="text-gray-900">{formatCurrency(estimatedRent)}</span>
                </div>
              </div>
              {hasLandlordProposedRent && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  {isLandlordHigher ? (
                    <div className="brand-card-soft p-4 mb-6">
                      <p className="text-gray-900 text-lg font-bold mb-2">
                        La hausse de votre propriétaire est <u>plus élevée</u> que celle estimée
                      </p>
                      <p className="text-gray-800 text-sm mb-3">
                        Votre propriétaire demande <strong>{formatCurrency(landlordProposedRentValue)}</strong> (+{formatCurrency(landlordProposedRentValue - currentRent)}),
                        alors que notre estimation est de <strong>{formatCurrency(estimatedRent)}</strong> (+{formatCurrency(breakdown.total)}).
                      </p>
                      <p className="text-gray-800 text-sm mb-4">
                        Différence: <strong>{formatCurrency(landlordProposedRentValue - estimatedRent)}</strong> de plus par mois
                      </p>
                      <p className="text-gray-900 font-semibold mb-3">Prochaines étapes:</p>
                      <ul className="text-gray-800 text-sm list-disc list-inside mb-4 space-y-1">
                        <li>
                          Vous avez le droit de refuser une augmentation de loyer. Le Tribunal administratif du logement offre un formulaire intitulé{" "}
                          <a target="_blank" rel="noreferrer" href="https://www.tal.gouv.qc.ca/sites/default/files/notices/TAL_810_E.pdf" className="underline">
                            Réponse du locataire à l’avis d’augmentation de loyer et de modification d’une autre condition du bail
                          </a>
                          , que vous pourrez imprimer et livrer à votre propriétaire par courrier recommandé. Souvenez-vous : vous devez répondre dans le mois suivant la réception de l'avis d'augmentation.
                        </li>
                        <li>Si vous avez besoin d'aide, contactez votre comité du logement local. Cliquez <a target="_blank" rel="noreferrer" href={t("footer.link2")} className="underline">ici</a> pour trouver celui qui est le plus proche de chez vous.</li>
                      </ul>
                      <p className="text-gray-900 font-semibold mb-2">Important</p>
                      <p className="text-gray-800 text-sm mb-2">
                        Cet outil est un estimateur. Confirmez les montants (taxes, assurances, travaux) avec le propriétaire. Certaines situations particulières sont plus complexes.
                      </p>
                      <p className="text-gray-800 text-sm mb-0">
                        Pour le calcul complet, utilisez <a target="_blank" rel="noreferrer" href={t("footer.link3")} className="underline">l'outil de calcul 2026 du TAL</a>. En cas de désaccord, seul le Tribunal administratif du logement peut trancher.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800 text-lg font-bold">
                        Bonne nouvelle!
                      </p>
                      <p className="text-green-700 text-sm mt-2">
                        La hausse proposée par votre propriétaire ({formatCurrency(landlordProposedRentValue)}) est égale ou inférieure à notre estimation ({formatCurrency(estimatedRent)}).
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {!isLandlordHigher && (() => {
              const showAssumptions = breakdown.insuranceUnknown || (prorata && prorata.method === 'estimated');
              if (showAssumptions) {
                return (
                  <div className="brand-card-soft p-4 mb-6 space-y-3">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t('2026.summary.assumptions', 'Informations manquantes')}
                      </h4>
                      <ul className="text-sm text-gray-800 list-disc list-inside space-y-1">
                        {breakdown.insuranceUnknown && (
                          <li>{t('2026.summary.insuranceUnknownNote', 'Assurances: le propriétaire pourrait avoir subi une augmentation des primes d\'assurance au-delà de l\'IPC, ce qui pourrait justifier un montant supplémentaire.')}</li>
                        )}
                        {prorata && prorata.method === 'estimated' && (
                          <li>{t('2026.summary.prorataEstimated', `Répartition estimée: ${prorata.shareLabel} (${prorata.totalUnits} logements)`)}</li>
                        )}
                      </ul>
                    </div>
                    <div className="border-t border-gray-200 pt-3">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t('legal.title', 'Avertissement')}
                      </h4>
                      <p className="text-sm text-gray-800 mb-2">
                        {t('legal.long1', "Estimation basée sur les informations disponibles et des hypothèses expliquées. Vous pouvez refuser l'augmentation de loyer dans le délai prévu.")}
                      </p>
                      <p className="text-sm text-gray-800">
                        {t('legal.long2', "Pour un désaccord, seul le Tribunal administratif du logement peut trancher.")}
                      </p>
                      <p className="text-sm text-gray-800 mt-2">
                        {t('legal.long3', "Il s'agit d'une estimation et d'un outil pour vous aider dans vos négociations avec votre propriétaire et n'a aucune valeur légale.")}
                      </p>
                    </div>
                  </div>
                );
              }
              return <LegalNoticeLong />;
            })()}

            <div className="mt-6 flex flex-col gap-3">
              <div className="space-y-3">
                <h2 className="text-2xl font-bold mb-5 text-left text-gray-700 w-full">
                  {t('about.title', 'À propos')}
                </h2>
                <p className="text-sm text-gray-700">
                  Fait par Nicolas Jolicoeur, conseiller d'arrondissement dans le Sud-Ouest à Montréal.
                  <br />
                  Visitez{" "}
                  <a href="https://jolicoeurmtl.com" target="_blank" rel="noreferrer" className="underline">jolicoeurmtl.com</a>{" "}
                  pour plus de détails.
                </p>
                <div className="loyer-follow-card">
                  <div className="loyer-profile-preview">
                    <img
                      src="/images/profil-cercle.png"
                      alt="Nicolas Jolicoeur"
                      className="loyer-profile-avatar"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="loyer-profile-copy">
                      <p className="loyer-profile-handle">
                        jolicoeur.mtl <span aria-hidden="true">✱</span>
                      </p>
                      <p className="loyer-profile-name">Nicolas Jolicoeur</p>
                    </div>
                  </div>
                  <div className="loyer-follow-links">
                    <h3 className="loyer-follow-title">{t('about.follow', 'Me suivre')}</h3>
                    <div className="loyer-follow-list">
                      <a href="https://instagram.com/jolicoeur.mtl" target="_blank" rel="noreferrer">
                        {t('about.insta', 'Instagram')} <span aria-hidden="true">✱</span>
                      </a>
                      <a href="https://facebook.com/jolicoeur.mtl" target="_blank" rel="noreferrer">
                        {t('about.facebook', 'Facebook')} <span aria-hidden="true">✱</span>
                      </a>
                      <a href="https://www.tiktok.com/@jolicoeur.mtl" target="_blank" rel="noreferrer">
                        {t('about.tiktok', 'TikTok')} <span aria-hidden="true">✱</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <BrandButton
                type="button"
                variant="outline"
                onClick={() => goToStep(STEPS.ADDRESS, { replaceHistory: true })}
              >
                {t('2026.summary.restart', 'Recommencer')}
              </BrandButton>
            </div>

          </div>
        )}

        <div className="mt-10 pt-6 border-t border-gray-200 text-sm text-gray-600 italic w-full">
          <p>
            Avec les données <a className="underline" href="https://donnees.montreal.ca/fr/dataset/taxes-municipales">Imposition annuelle de taxes municipales</a>, <a className="underline" href="https://donnees.montreal.ca/fr/dataset/permis-construction">Permis de construction, transformation et démolition</a> et <a className="underline" href="https://donnees.montreal.ca/fr/dataset/unites-evaluation-fonciere">Unités d'évaluation foncière</a>, tirées du site web des données ouvertes de la Ville de Montréal, 24 février 2026.
          </p>
        </div>
      </form>
    </FormProvider>
  );
};

export default RentForm2026;
